"use client"

import { useState, useEffect, useContext } from "react"
import { AuthContext } from "../../context/AuthContext"
import api from "../../services/api"
import {
  FaTimes,
  FaLink,
  FaEdit,
  FaLock,
  FaCalendar,
  FaQrcode,
  FaPalette,
  FaImage,
  FaEye,
  FaEyeSlash,
} from "react-icons/fa"

export default function CreateLinkModal({ show, onClose, onCreated }) {
  const { user: jwtUser } = useContext(AuthContext)

  const [user, setUser] = useState(null)
  const [plans, setPlans] = useState(null)

  const [originalUrl, setOriginalUrl] = useState("")
  const [customSlug, setCustomSlug] = useState("")
  const [password, setPassword] = useState("")
  const [expirationDate, setExpirationDate] = useState("")

  const [showPassword, setShowPassword] = useState(false)

  const [generateQr, setGenerateQr] = useState(false)
  const [qrColor, setQrColor] = useState("#000000")
  const [qrLogo, setQrLogo] = useState("")
  const [qrFrame, setQrFrame] = useState("")
  const [qrTemplate, setQrTemplate] = useState("")

  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState("")

  useEffect(() => {
    if (!jwtUser?.id) return

    const fetchData = async () => {
      try {
        const [userRes, planRes] = await Promise.all([api.get("/user/me"), api.get("/subscription/plans")])
        setUser(userRes.data.data)
        setPlans(planRes.data)
      } catch (err) {
        console.error(err)
      }
    }

    fetchData()
  }, [jwtUser])

  const activePlan = user && plans ? plans[user.plan] : null
  const limit = activePlan?.limit ?? 0
  const used = user?.linkCount ?? 0
  const hasReachedLimit = limit !== -1 && used >= limit

  const qrFeatures = activePlan?.features?.qrCodes || {}
  const canUseQR = qrFeatures.instantQr === true
  const canColor = canUseQR && qrFeatures.colorOptions
  const canLogo = canUseQR && qrFeatures.customLogo
  const canFrame = canUseQR && qrFeatures.customFrames
  const canTemplate = canUseQR && qrFeatures.brandingTemplates

  const normalizeUrl = (url) => {
  if (!url) return url

  // Trim spaces
  let cleaned = url.trim()

  // If already has protocol, return as-is
  if (/^https?:\/\//i.test(cleaned)) {
    return cleaned
  }

  // Otherwise, prepend https://
  return `https://${cleaned}`
}

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setMessage("")

    try {
      if (!user || !activePlan) throw new Error("User or plan data not loaded")
      if (hasReachedLimit) {
        setMessage(`You have reached your ${activePlan.title} plan limit of ${limit} links.`)
        return
      }

      const normalizedUrl = normalizeUrl(originalUrl)

      let parsedUrl
      try {
        parsedUrl = new URL(normalizedUrl)
      } catch {
        setMessage("Please enter a valid URL")
        setLoading(false)
        return
      }

      // ❗ Block domains without a dot (e.g. "dem")
      if (!parsedUrl.hostname.includes(".")) {
        setMessage("Please enter a valid domain (e.g. example.com)")
        setLoading(false)
        return
      }


      const payload = {
        originalUrl: normalizedUrl,
      }

      if (activePlan.features?.customizeUrls && customSlug) payload.customUrl = customSlug
      if (activePlan.features?.passwordProtection && password) payload.password = password
      if (activePlan.features?.urlExpiration && expirationDate) payload.expiresAt = expirationDate

      if (canUseQR && generateQr) {
        const qrStyle = {}
        if (canColor && qrColor) qrStyle.color = qrColor
        if (canLogo && qrLogo) qrStyle.logo = qrLogo
        if (canFrame && qrFrame) qrStyle.frame = qrFrame
        if (canTemplate && qrTemplate) qrStyle.template = qrTemplate

        if (Object.keys(qrStyle).length > 0) {
          payload.qrStyle = qrStyle
        }
      }

      const res = await api.post("/links", payload)
      onCreated(res.data)

      setOriginalUrl("")
      setCustomSlug("")
      setPassword("")
      setExpirationDate("")
      setGenerateQr(false)
      setQrColor("#000000")
      setQrLogo("")
      setQrFrame("")
      setQrTemplate("")
      onClose()
    } catch (err) {
      setMessage(err.response?.data?.message || err.message || "Failed to create link")
    } finally {
      setLoading(false)
    }
  }

  if (!show) return null

  const inputStyle = {
    width: "100%",
    padding: "12px 14px 12px 44px",
    border: "1px solid #e2e8f0",
    borderRadius: "8px",
    fontSize: "15px",
    transition: "all 0.2s ease",
    background: "#f8fafc",
  }

  const iconStyle = {
    position: "absolute",
    left: "14px",
    top: "50%",
    transform: "translateY(-50%)",
    color: "#64748b",
  }

  return (
    <>
      <style>{`
        .modal-backdrop {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(15, 23, 42, 0.6);
          backdrop-filter: blur(4px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1080;
        }
        .modal-dialog {
          max-width: 560px;
          width: 90%;
          max-height: 90vh;
          overflow-y: auto;
          animation: slideUp 0.2s ease-out;
        }
        .modal-content {
          background: white;
          border-radius: 12px;
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
          overflow: hidden;
        }
        .modal-header {
          background: #1e293b;
          padding: 20px 24px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .modal-body {
          padding: 24px;
        }
        .form-input:focus {
          outline: none;
          border-color: #3b82f6;
          background: white;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }
        .form-input:disabled {
          background: #f1f5f9;
          cursor: not-allowed;
        }
        .options-section {
          background: #f8fafc;
          padding: 20px;
          border-radius: 8px;
          border: 1px solid #e2e8f0;
          margin-bottom: 20px;
        }
        .qr-checkbox {
          display: flex;
          align-items: center;
          padding: 12px;
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        .qr-checkbox:hover {
          border-color: #3b82f6;
        }
        .btn-primary {
          background: #3b82f6;
          color: white;
          border: none;
          padding: 12px 24px;
          border-radius: 8px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        .btn-primary:hover:not(:disabled) {
          background: #2563eb;
        }
        .btn-primary:disabled {
          background: #94a3b8;
          cursor: not-allowed;
        }
        .btn-secondary {
          background: white;
          color: #64748b;
          border: 1px solid #e2e8f0;
          padding: 12px 24px;
          border-radius: 8px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        .btn-secondary:hover {
          background: #f8fafc;
        }
        .close-btn {
          background: rgba(255, 255, 255, 0.1);
          border: none;
          color: white;
          width: 32px;
          height: 32px;
          border-radius: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: background 0.2s ease;
        }
        .close-btn:hover {
          background: rgba(255, 255, 255, 0.2);
        }
        .alert-error {
          background: #fef2f2;
          border: 1px solid #fecaca;
          color: #dc2626;
          padding: 12px 16px;
          border-radius: 8px;
          margin-bottom: 16px;
          font-size: 14px;
        }
        .usage-info {
          background: #fffbeb;
          border: 1px solid #fde68a;
          color: #b45309;
          padding: 12px 16px;
          border-radius: 8px;
          margin-bottom: 16px;
          font-size: 14px;
          font-weight: 500;
        }
        .spinner {
          width: 16px;
          height: 16px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-top-color: white;
          border-radius: 50%;
          animation: spin 0.6s linear infinite;
          margin-right: 8px;
        }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>

      <div className="modal-backdrop" onClick={onClose}>
        <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
          <div className="modal-content">
            <div className="modal-header">
              <h5 style={{ margin: 0, color: "white", fontSize: "18px", fontWeight: "600" }}>Create New Short Link</h5>
              <button className="close-btn" onClick={onClose}>
                <FaTimes size={14} />
              </button>
            </div>

            <div className="modal-body">
              {message && <div className="alert-error">{message}</div>}

              {activePlan && (
                <div className="usage-info">
                  Links used: <strong>{used}</strong> / <strong>{limit === -1 ? "Unlimited" : limit}</strong>
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div style={{ marginBottom: "16px" }}>
                  <label
                    style={{
                      display: "block",
                      fontWeight: "500",
                      color: "#374151",
                      marginBottom: "6px",
                      fontSize: "14px",
                    }}
                  >
                    Original URL
                  </label>
                  <div style={{ position: "relative" }}>
                    <FaLink style={iconStyle} size={14} />
                    <input
                      type="text"
                      className="form-input"
                      style={inputStyle}
                      value={originalUrl}
                      onChange={(e) => setOriginalUrl(e.target.value)}
                      placeholder="example.com or https://example.com"
                      required
                      disabled={hasReachedLimit}
                    />
                  </div>
                </div>

                <div className="options-section">
                  <h6 style={{ fontWeight: "600", color: "#1e293b", marginBottom: "16px", fontSize: "14px" }}>
                    Additional Options
                  </h6>

                  {activePlan?.features?.customizeUrls && (
                    <div style={{ marginBottom: "14px" }}>
                      <label
                        style={{
                          display: "block",
                          fontWeight: "500",
                          color: "#374151",
                          marginBottom: "6px",
                          fontSize: "13px",
                        }}
                      >
                        Custom Slug <span style={{ color: "#94a3b8" }}>(optional)</span>
                      </label>
                      <div style={{ position: "relative" }}>
                        <FaEdit style={iconStyle} size={14} />
                        <input
                          type="text"
                          className="form-input"
                          style={inputStyle}
                          value={customSlug}
                          onChange={(e) => setCustomSlug(e.target.value)}
                          placeholder="my-custom-link"
                        />
                      </div>
                    </div>
                  )}

                  {activePlan?.features?.passwordProtection && (
                    <div style={{ marginBottom: "14px" }}>
                      <label
                        style={{
                          display: "block",
                          fontWeight: "500",
                          color: "#374151",
                          marginBottom: "6px",
                          fontSize: "13px",
                        }}
                      >
                        Password Protection <span style={{ color: "#94a3b8" }}>(optional)</span>
                      </label>
                      <div style={{ position: "relative" }}>
                        <FaLock style={iconStyle} size={14} />
                        <input
                          type={showPassword ? "text" : "password"}
                          className="form-input"
                          style={{ ...inputStyle, paddingRight: "44px" }}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder="Enter password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          style={{
                            position: "absolute",
                            right: "14px",
                            top: "50%",
                            transform: "translateY(-50%)",
                            background: "none",
                            border: "none",
                            color: "#64748b",
                            cursor: "pointer",
                          }}
                        >
                          {showPassword ? <FaEyeSlash size={14} /> : <FaEye size={14} />}
                        </button>
                      </div>
                    </div>
                  )}

                  {activePlan?.features?.urlExpiration && (
                    <div style={{ marginBottom: "14px" }}>
                      <label
                        style={{
                          display: "block",
                          fontWeight: "500",
                          color: "#374151",
                          marginBottom: "6px",
                          fontSize: "13px",
                        }}
                      >
                        Expiration Date <span style={{ color: "#94a3b8" }}>(optional)</span>
                      </label>
                      <div style={{ position: "relative" }}>
                        <FaCalendar style={iconStyle} size={14} />
                        <input
                          type="date"
                          className="form-input"
                          style={inputStyle}
                          value={expirationDate}
                          onChange={(e) => setExpirationDate(e.target.value)}
                        />
                      </div>
                    </div>
                  )}

                  {canUseQR && (
                    <label className="qr-checkbox">
                      <input
                        type="checkbox"
                        checked={generateQr}
                        onChange={(e) => setGenerateQr(e.target.checked)}
                        style={{ width: "18px", height: "18px", marginRight: "10px" }}
                      />
                      <FaQrcode style={{ marginRight: "8px", color: "#3b82f6" }} />
                      <span style={{ fontWeight: "500", color: "#1e293b" }}>Generate Instant QR Code</span>
                    </label>
                  )}

                  {generateQr && canColor && (
                    <div style={{ marginTop: "14px" }}>
                      <label
                        style={{
                          display: "block",
                          fontWeight: "500",
                          color: "#374151",
                          marginBottom: "6px",
                          fontSize: "13px",
                        }}
                      >
                        <FaPalette style={{ marginRight: "6px" }} />
                        QR Color
                      </label>
                      <input
                        type="color"
                        value={qrColor}
                        onChange={(e) => setQrColor(e.target.value)}
                        style={{
                          width: "100%",
                          height: "40px",
                          borderRadius: "6px",
                          border: "1px solid #e2e8f0",
                          cursor: "pointer",
                        }}
                      />
                    </div>
                  )}

                  {generateQr && canLogo && (
                    <div style={{ marginTop: "14px" }}>
                      <label
                        style={{
                          display: "block",
                          fontWeight: "500",
                          color: "#374151",
                          marginBottom: "6px",
                          fontSize: "13px",
                        }}
                      >
                        <FaImage style={{ marginRight: "6px" }} />
                        QR Logo URL <span style={{ color: "#94a3b8" }}>(optional)</span>
                      </label>
                      <div style={{ position: "relative" }}>
                        <FaImage style={iconStyle} size={14} />
                        <input
                          type="text"
                          className="form-input"
                          style={inputStyle}
                          value={qrLogo}
                          onChange={(e) => setQrLogo(e.target.value)}
                          placeholder="https://example.com/logo.png"
                        />
                      </div>
                    </div>
                  )}
                </div>

                <div style={{ display: "flex", justifyContent: "flex-end", gap: "12px" }}>
                  <button type="button" className="btn-secondary" onClick={onClose} disabled={loading}>
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary" disabled={loading || hasReachedLimit}>
                    {loading ? (
                      <>
                        <span className="spinner" />
                        Creating...
                      </>
                    ) : hasReachedLimit ? (
                      "Limit Reached"
                    ) : (
                      "Create Link"
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}